<template>
  <div v-frag>
    <b-row>
      <b-colxx xxs="12">
        <piaf-breadcrumb :heading="$t('menu.edit')" />
        <div class="separator mb-5"></div>
      </b-colxx>
    </b-row>
    <b-row>
      <b-colxx xxs="12">
        <b-card class="mb-4">
          <b-form @submit.prevent="onSubmit" class="av-tooltip">
            <b-form-group :label="`${$t('anyform.name')} (EN)`">
              <b-form-input
                type="text"
                v-model="$v.form.name_en.$model"
                :state="!$v.form.name_en.$error"
                :disabled="isLoading"
              />
              <b-form-invalid-feedback v-if="!$v.form.name_en.required">
                {{ $t('formErrors.enterName') }}
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group :label="`${$t('anyform.name')} (RU)`">
              <b-form-input
                type="text"
                v-model="$v.form.name_ru.$model"
                :state="!$v.form.name_ru.$error"
                :disabled="isLoading"
              />
              <b-form-invalid-feedback v-if="!$v.form.name_ru.required">
                {{ $t('formErrors.enterName') }}
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group :label="$t('pages.promocodes.startDate')" class="mb-3">
              <datepicker :bootstrap-styling="true" v-model="$v.form.start.$model" :disabled="isLoading"></datepicker>
              <div
                :class="{
                  'invalid-feedback': true,
                  'd-block': $v.form.start.$error && !$v.form.start.required
                }"
              >
                {{ $t('formErrors.enterStartDate') }}
              </div>
            </b-form-group>

            <b-form-group :label="$t('pages.promocodes.endDate')" class="mb-3">
              <datepicker :bootstrap-styling="true" v-model="$v.form.end.$model" :disabled="isLoading"></datepicker>
              <div
                :class="{
                  'invalid-feedback': true,
                  'd-block': $v.form.end.$error && !$v.form.end.required
                }"
              >
                {{ $t('formErrors.enterEndDate') }}
              </div>
            </b-form-group>

            <b-form-group :label="$t('pages.promocodes.code')">
              <b-form-input
                type="text"
                v-model="$v.form.code.$model"
                :state="!$v.form.code.$error"
                :disabled="isLoading"
              />
              <b-form-invalid-feedback v-if="!$v.form.code.required">
                {{ $t('formErrors.enterCode') }}
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group :label="$t('pages.promocodes.sale')">
              <b-form-input
                type="text"
                v-model="$v.form.sale.$model"
                :state="!$v.form.sale.$error"
                :disabled="isLoading"
              />
              <b-form-invalid-feedback v-if="!$v.form.sale.required">
                {{ $t('formErrors.enterSale') }}
              </b-form-invalid-feedback>
              <b-form-invalid-feedback v-else-if="!$v.form.sale.numeric">
                {{ $t('formErrors.mustBeNumber') }}
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label="Which is active?">
              <v-select v-model="active" :options="activeOptions" :disabled="isLoading" />
            </b-form-group>

            <b-form-group v-if="active === 'Categories'" label="Choose category">
              <v-select
                v-model="form.category_id"
                :options="categories"
                :reduce="(ct) => ct.id"
                label="name"
                :disabled="isLoading"
              />
            </b-form-group>

            <b-form-group v-else-if="active === 'Products'" label="Choose product">
              <v-select
                v-model="form.product_id"
                :options="products"
                :reduce="(product) => product.id"
                label="name"
                :disabled="isLoading"
              />
            </b-form-group>

            <b-button
              type="submit"
              variant="secondary"
              size="lg"
              :disabled="isLoading || componentLoading"
              :class="{
                'btn-multiple-state btn-shadow mt-2': true,
                'show-spinner': isLoading || componentLoading
              }"
            >
              <span class="spinner d-inline-block">
                <span class="bounce1"></span>
                <span class="bounce2"></span>
                <span class="bounce3"></span>
              </span>
              <span class="label text-uppercase">
                {{ $t('buttons.save') }}
              </span>
            </b-button>
          </b-form>
        </b-card>
      </b-colxx>
    </b-row>
  </div>
</template>

<script>
import Datepicker from 'vuejs-datepicker'
import { handleArrayDataInternalization, getDateWithFormat } from '@/utils'
const { required, numeric } = require('vuelidate/lib/validators')

export default {
  components: { Datepicker },
  data: () => ({
    componentLoading: false,
    active: null,
    activeOptions: ['Categories', 'Products', 'None'],
    form: {
      name_en: '',
      name_ru: '',
      code: '',
      sale: '',
      start: new Date(),
      end: new Date(),
      category_id: '',
      product_id: ''
    }
  }),
  validations: {
    form: {
      name_en: { required },
      name_ru: { required },
      code: { required },
      sale: { required, numeric },
      start: { required },
      end: { required }
    }
  },
  computed: {
    isLoading() {
      return this.$store.getters['promocodes/isLoading']
    },
    current() {
      return this.$route.params.id
    },
    categories() {
      const requirements = this.$store.getters['promocodes/requirements']
      return handleArrayDataInternalization(requirements.categories, ['name'], this.$i18n.locale)
    },
    products() {
      const requirements = this.$store.getters['promocodes/requirements']
      return handleArrayDataInternalization(requirements.products, ['name'], this.$i18n.locale)
    }
  },
  watch: {
    active(_, prev) {
      if (prev) {
        this.form.category_id = ''
        this.form.product_id = ''
      }
    }
  },
  async mounted() {
    await this.$store.dispatch('promocodes/fetchPromocode', {
      id: this.current,
      vm: this
    })
    await this.$store.dispatch('promocodes/fetchRequirements')
  },
  methods: {
    addNotification(type, title, message) {
      this.$notify(type, title, message, { duration: 2000, permanent: false })
    },
    onSubmit() {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        const payload = {
          ...this.form,
          category_id: this.form.category_id || '',
          product_id: this.form.product_id || '',
          start: getDateWithFormat(this.form.start),
          end: getDateWithFormat(this.form.end)
        }

        this.$store.dispatch('promocodes/updatePromocode', {
          id: this.current,
          payload,
          vm: this
        })
      }
    },
    clearForm() {
      this.editForm.name_en = ''
      this.editForm.name_ru = ''
      this.editForm.code = ''
      this.editForm.sale = ''
      this.editForm.start = new Date()
      this.editForm.end = new Date()
      this.editForm.category_id = ''
      this.editForm.product_id = ''
    }
  }
}
</script>
